import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { PurchaseOrderReceiptDispositionStatus } from '../../enums/purchase-order-receipt-disposition-status.enum';
import { BaseEntity } from './base.entity';
import { ItemLotEntity } from './item-lot.entity';
import { PurchaseOrderDetailEntity } from './purchase-order-detail.entity';
import type { PurchaseOrderReceiptExceptionEntity } from './purchase-order-receipt-exception.entity';
import { PurchaseOrderReceiptEntity } from './purchase-order-receipt.entity';

@Index(
	'pord__fk_purchase_order_detail__id__deleted_userId',
	['deletedUserId', 'fkPurchaseOrderDetailId'],
	{},
)
@Index(
	'purchase_order_receipt_detail__fk_item_lot__id__deleted_userId',
	['deletedUserId', 'fkItemLotId'],
	{},
)
@Index('purchase_order_receipt_detail__fk_item_lot__id_fk', ['fkItemLotId'], {})
@Index(
	'purchase_order_receipt_detail__fk_purchase_order_detail__id_fk',
	['fkPurchaseOrderDetailId'],
	{},
)
@Index(
	'purchase_order_receipt_detail__fk_purchase_order_receipt__id_fk',
	['fkPurchaseOrderReceiptId'],
	{},
)
// PD-17290: the integrations upsert receipt lines by externalId. Without
// this index every lookup scanned the whole table, so a large reimport
// slowed down as it went and timed out. Same shape as purchase_order_detail.
@Index(
	'purchase_order_receipt_detail_externalId__deleted_userId',
	['externalId', 'deletedUserId'],
	{},
)
@Entity('purchase_order_receipt_detail')
export class PurchaseOrderReceiptDetailEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', length: 255 })
	public externalId!: string;

	/**
	 * Total physically received on this line for this receipt event.
	 * Invariant (enforced server-side):
	 *   quantityReceived = quantityAccepted + quantityDamaged + quantityOver
	 *
	 * Pre-existing column — kept as a denormalized rollup so the
	 * existing inventory sync code (which reads `quantityReceived`)
	 * continues to work without changes. SHORT / REFUSED / WRONG_ITEM
	 * quantities deliberately do NOT contribute because we never took
	 * possession.
	 */
	@Column('int', { name: 'quantityReceived', nullable: true })
	public quantityReceived!: number | null;

	/** Goes to sellable stock (regular bin). */
	@Column('int', {
		name: 'quantityAccepted',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityAccepted!: number;

	/** Goes to a QUARANTINE bin (LocationEntity.subType=QUARANTINE). */
	@Column('int', {
		name: 'quantityDamaged',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityDamaged!: number;

	/** Vendor over-shipped; we kept it (sellable bin), but the row is flagged via an OVER exception. */
	@Column('int', {
		name: 'quantityOver',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityOver!: number;

	/**
	 * Vendor under-shipped vs the packing slip. NO inventory movement —
	 * we never took possession of these. Recorded for vendor scorecard
	 * and credit chase.
	 */
	@Column('int', {
		name: 'quantityShort',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityShort!: number;

	/** Refused at the dock. NO inventory movement. */
	@Column('int', {
		name: 'quantityRefused',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityRefused!: number;

	/** Wrong item — refused. NO inventory movement. */
	@Column('int', {
		name: 'quantityWrongItem',
		unsigned: true,
		default: () => "'0'",
	})
	public quantityWrongItem!: number;

	/**
	 * Derived rollup over this detail's exception statuses. Recomputed
	 * by the service on any exception mutation. See
	 * `libs/core/src/utils/purchaseOrderReceiptDispositionStatus.ts`.
	 */
	@Column('varchar', {
		name: 'dispositionStatus',
		length: 32,
		default: () => `'${PurchaseOrderReceiptDispositionStatus.CLEAN}'`,
	})
	public dispositionStatus!: PurchaseOrderReceiptDispositionStatus;

	@Column('int', { name: '_fk_purchase_order_receipt__id', unsigned: true })
	public fkPurchaseOrderReceiptId!: number;

	@Column('int', { name: '_fk_purchase_order_detail__id', unsigned: true })
	public fkPurchaseOrderDetailId!: number;

	// PD-17273: nullable. Receiving in Workd always creates a lot, but receipts
	// imported from an ERP without lot tracking (Prophet 21) have none, and the
	// NOT NULL column made every imported receipt line fail its foreign key.
	@Column('int', { name: '_fk_item_lot__id', unsigned: true, nullable: true })
	public fkItemLotId!: number | null;

	@ManyToOne(
		() => ItemLotEntity,
		(itemLot) => itemLot.purchaseOrderReceiptDetails,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
			nullable: true,
		},
	)
	@JoinColumn([{ name: '_fk_item_lot__id', referencedColumnName: 'id' }])
	public fkItemLot!: ItemLotEntity | null;

	@ManyToOne(
		() => PurchaseOrderDetailEntity,
		(purchaseOrderDetail) => purchaseOrderDetail.purchaseOrderReceiptDetails,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{ name: '_fk_purchase_order_detail__id', referencedColumnName: 'id' },
	])
	public fkPurchaseOrderDetail!: PurchaseOrderDetailEntity;

	@ManyToOne(
		() => PurchaseOrderReceiptEntity,
		(purchaseOrderReceipt) => purchaseOrderReceipt.purchaseOrderReceiptDetails,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{ name: '_fk_purchase_order_receipt__id', referencedColumnName: 'id' },
	])
	public fkPurchaseOrderReceipt!: PurchaseOrderReceiptEntity;

	/**
	 * Exceptions raised against this detail line — DAMAGED, SHORT,
	 * OVER, etc. One per non-zero exception bucket per save. Drives
	 * the dispositionStatus rollup on this row and on the parent
	 * receipt. Inverse defined on PurchaseOrderReceiptExceptionEntity
	 * via fkReceiptDetail.
	 */
	@OneToMany(
		'PurchaseOrderReceiptExceptionEntity',
		(exception: PurchaseOrderReceiptExceptionEntity) =>
			exception.fkReceiptDetail,
	)
	public purchaseOrderReceiptExceptions!: Array<PurchaseOrderReceiptExceptionEntity>;
}
